import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Scan, Sparkles, Loader2, AlertTriangle, AlertCircle, RefreshCw, TrendingUp, Info, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { FindingTxnRow } from "@/components/FindingTxnRow";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_CONFIG = {
  DUPLICATE: { icon: AlertTriangle, color: "text-red-600 bg-red-50 border-red-200", label: "Duplicate" },
  CATEGORY_MISMATCH: { icon: AlertCircle, color: "text-amber-600 bg-amber-50 border-amber-200", label: "Category Mismatch" },
  RECURRING_INCONSISTENCY: { icon: RefreshCw, color: "text-blue-600 bg-blue-50 border-blue-200", label: "Recurring Inconsistency" },
  UNUSUAL_AMOUNT: { icon: TrendingUp, color: "text-purple-600 bg-purple-50 border-purple-200", label: "Unusual Amount" },
  MISSING_INFO: { icon: Info, color: "text-stone-600 bg-stone-50 border-stone-200", label: "Missing Info" },
};

const FindingCard = ({ f, idx, transactions, categoriesFor, resolved, onResolve, onReopen, onDataChanged }) => {
  const tc = TYPE_CONFIG[f.type] || TYPE_CONFIG.MISSING_INFO;
  const Icon = tc.icon;
  const sevColor = f.severity === "high" ? "bg-red-500" : f.severity === "medium" ? "bg-amber-500" : "bg-stone-400";
  const matched = (f.transaction_ids || []).map(id => transactions[id]).filter(Boolean);
  const isResolved = resolved || (!f.previously_resolved && (f.transaction_ids || []).length > 0 && matched.length === 0);

  return (
    <Card className={`border transition-opacity duration-300 ${isResolved ? "border-emerald-200 opacity-60" : tc.color.split(" ")[2]}`} data-testid={`finding-${idx}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`p-1.5 rounded-lg ${tc.color.split(" ").slice(1, 3).join(" ")}`}><Icon className={`w-4 h-4 ${tc.color.split(" ")[0]}`} /></div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border ${tc.color}`}>{tc.label}</span>
              <span className="flex items-center gap-1"><span className={`w-2 h-2 rounded-full ${sevColor}`} /><span className="text-[10px] text-stone-500 capitalize">{f.severity} severity</span></span>
              {f.previously_resolved && !isResolved ? (
                <span className="ml-auto flex items-center gap-2">
                  <span className="text-[10px] font-semibold text-stone-600 bg-stone-100 border border-stone-200 px-2 py-0.5 rounded-full" data-testid={`finding-prev-resolved-${idx}`}>Previously resolved</span>
                  <button className="text-xs font-medium text-stone-700 hover:text-stone-900 hover:underline underline-offset-2" onClick={() => onReopen(f)} data-testid={`finding-reopen-${idx}`}>Reopen</button>
                  <button className="text-xs font-medium text-stone-600 hover:text-emerald-700 hover:underline underline-offset-2" onClick={() => onResolve(f)} data-testid={`finding-mark-resolved-${idx}`}>Keep resolved</button>
                </span>
              ) : isResolved ? (
                <span className="ml-auto flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full" data-testid={`finding-resolved-badge-${idx}`}><CheckCircle2 className="w-3 h-3" /> Resolved</span>
              ) : (
                <button className="ml-auto text-xs font-medium text-stone-600 hover:text-emerald-700 hover:underline underline-offset-2" onClick={() => onResolve(f)} data-testid={`finding-mark-resolved-${idx}`}>Mark resolved</button>
              )}
            </div>
            <h4 className="text-sm font-semibold text-stone-900 mb-1">{f.title}</h4>
            <p className="text-xs text-stone-600 mb-2">{f.detail}</p>
            {f.suggestion && <p className="text-xs text-stone-500 bg-stone-50 rounded-lg px-3 py-2"><span className="font-medium text-stone-700">Suggestion:</span> {f.suggestion}</p>}
            {!isResolved && matched.length > 0 && (
              <div className="mt-2 space-y-1">
                <p className="text-[10px] font-medium text-stone-500 uppercase tracking-wide">Related Transactions</p>
                {matched.map(t => (
                  <FindingTxnRow key={t.id} txn={t} findingIdx={idx} categories={categoriesFor(t.type)} isDuplicate={f.type === "DUPLICATE"} siblings={matched}
                    onChanged={(kind) => { onDataChanged(); if (kind === "keep") onResolve(f, true); }} />
                ))}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const AIAnalysisPanel = ({ totalCount, categoriesFor, onDataChanged }) => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resolved, setResolved] = useState([]);
  const [typeFilter, setTypeFilter] = useState("all");
  const [includeResolved, setIncludeResolved] = useState(false);

  const loadLatest = (inc = includeResolved) => axios.get(`${API}/transactions/ai-analysis/latest`, { params: { include_resolved: inc } }).then(r => { if (r.data) { setResult(r.data); setResolved([]); } }).catch(() => {});
  useEffect(() => { loadLatest(false); }, []);
  const toggleInclude = () => { const v = !includeResolved; setIncludeResolved(v); if (result) loadLatest(v); };
  const txnMap = result?.transactions || {};
  const dataChanged = () => {
    onDataChanged();
    axios.get(`${API}/transactions/ai-analysis/latest`, { params: { include_resolved: true } }).then(r => r.data && setResult(prev => ({ ...prev, transactions: r.data.transactions }))).catch(() => {});
  };

  const run = async () => {
    setLoading(true);
    try { setResult((await axios.post(`${API}/transactions/ai-analysis`, null, { params: { include_resolved: includeResolved } })).data); setResolved([]); setTypeFilter("all"); }
    catch (e) { toast.error("Analysis failed. Please try again."); }
    finally { setLoading(false); }
  };

  const resolve = async (f, silent) => {
    setResolved(r => r.includes(f.signature) ? r : [...r, f.signature]);
    try { await axios.post(`${API}/transactions/ai-analysis/dismiss`, { signature: f.signature }); if (!silent) toast.success("Marked resolved"); }
    catch (e) { toast.error("Could not save resolution"); }
  };

  const reopen = async (f) => {
    try {
      await axios.post(`${API}/transactions/ai-analysis/reopen`, { signature: f.signature });
      setResult(prev => ({ ...prev, findings: prev.findings.map(x => x.signature === f.signature ? { ...x, previously_resolved: false } : x) }));
      setResolved(r => r.filter(s => s !== f.signature));
      toast.success("Finding reopened");
    } catch (e) { toast.error("Could not reopen"); }
  };

  const findings = result?.findings || [];
  const openCount = findings.filter(f => !f.previously_resolved && !resolved.includes(f.signature)).length;
  const prevCount = findings.filter(f => f.previously_resolved).length;
  const counts = findings.reduce((a, f) => ({ ...a, [f.type]: (a[f.type] || 0) + 1 }), {});
  const shown = typeFilter === "all" ? findings : findings.filter(f => f.type === typeFilter);

  return (
    <div className="space-y-6" data-testid="ai-analysis-tab">
      <Card className="border-stone-200/60">
        <CardContent className="p-6 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-stone-900 flex items-center gap-2"><Scan className="w-5 h-5 text-stone-500" /> AI Transaction Analysis</h3>
            <p className="text-sm text-stone-500 mt-1">Scans every transaction for duplicates, category mismatches, recurring inconsistencies, unusual amounts and missing details. Resolved items stay hidden on future runs.</p>
            {result?.created_at && <p className="text-xs text-stone-400 mt-1" data-testid="analysis-last-run">Last run {new Date(result.created_at).toLocaleString()}</p>}
          </div>
          <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-stone-600 cursor-pointer whitespace-nowrap" data-testid="include-resolved-label">
            <Checkbox checked={includeResolved} onCheckedChange={toggleInclude} data-testid="include-resolved-checkbox" /> Include resolved
          </label>
          <Button data-testid="run-analysis-btn" onClick={run} disabled={loading} className="bg-stone-900 text-white hover:bg-stone-800 whitespace-nowrap">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Analyzing...</> : <><Sparkles className="w-4 h-4 mr-2" /> Run Analysis</>}
          </Button>
          </div>
        </CardContent>
      </Card>

      {loading && (
        <div className="text-center py-16 bg-white border border-stone-200/60 rounded-xl">
          <Loader2 className="w-8 h-8 animate-spin text-stone-400 mx-auto mb-3" />
          <p className="text-sm text-stone-600 font-medium">Analyzing {result?.total_transactions || totalCount} transactions...</p>
          <p className="text-xs text-stone-400 mt-1">Large histories are checked in batches, this can take a minute</p>
        </div>
      )}

      {result && !loading && (
        <div className="space-y-4">
          <Card className="border-stone-200/60"><CardContent className="p-4"><p className="text-sm text-stone-700" data-testid="analysis-summary">
            {openCount > 0 ? `${openCount} open issue${openCount !== 1 ? "s" : ""}` : "No open issues"} across {result.total_transactions} transactions
            {resolved.length > 0 && ` · ${resolved.length} resolved this session`}{prevCount > 0 && ` · showing ${prevCount} previously resolved`}.
          </p></CardContent></Card>
          {findings.length > 0 ? (
            <>
              <div className="flex items-center gap-2 flex-wrap" data-testid="analysis-type-filters">
                {["all", ...Object.keys(counts)].map(t => (
                  <button key={t} onClick={() => setTypeFilter(t)} data-testid={`analysis-filter-${t}`}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${typeFilter === t ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-600 border-stone-200 hover:border-stone-400"}`}>
                    {t === "all" ? `All (${findings.length})` : `${TYPE_CONFIG[t]?.label || t} (${counts[t]})`}
                  </button>
                ))}
              </div>
              <div className="space-y-3">
                {shown.map(f => (
                  <FindingCard key={f.signature} f={f} idx={findings.indexOf(f)} transactions={txnMap} categoriesFor={categoriesFor}
                    resolved={resolved.includes(f.signature)} onResolve={resolve} onReopen={reopen} onDataChanged={dataChanged} />
                ))}
              </div>
            </>
          ) : (
            <Card className="border-emerald-200 bg-emerald-50">
              <CardContent className="p-6 text-center">
                <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                <p className="text-sm font-semibold text-emerald-800">All Clear</p>
                <p className="text-xs text-emerald-600 mt-1">No open issues in your transactions</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};
